import { Controller } from "@hotwired/stimulus";

// Add Existing Session picker on a group's Sessions tab. Typing (debounced)
// or changing the scope fetches server-rendered rows; ticks are kept in a
// Set so they survive a new search, mirrored into hidden event_ids[] inputs
// the form posts, and counted on the submit button.
export default class extends Controller {
  static targets = ["form", "searchInput", "scopeSelect", "results", "selectedInputs", "submitButton", "candidate"];
  static values = {
    searchUrl: String,
    searchDelay: { type: Number, default: 250 }
  };

  connect() {
    this.selected = new Map();
    this.searchTimer = null;
    this.abortController = null;
  }

  disconnect() {
    this.cancelSearch();
  }

  modalShown() {
    this.searchInputTarget.focus();
    this.fetchResults();
  }

  modalHidden() {
    this.cancelSearch();
    this.selected.clear();
    this.searchInputTarget.value = "";
    this.scopeSelectTarget.selectedIndex = 0;
    this.resultsTarget.innerHTML = "";
    this.syncSelection();
  }

  search() {
    clearTimeout(this.searchTimer);
    this.searchTimer = setTimeout(() => this.fetchResults(), this.searchDelayValue);
  }

  // Enter in the search box would submit the form with whatever is ticked;
  // adding is the button's job.
  preventSubmitOnEnter(event) {
    if (event.key === "Enter") event.preventDefault();
  }

  toggle(event) {
    const checkbox = event.currentTarget;
    if (checkbox.checked) {
      this.selected.set(checkbox.value, checkbox.dataset.title || "");
    } else {
      this.selected.delete(checkbox.value);
    }
    this.syncSelection();
  }

  candidateTargetConnected(checkbox) {
    checkbox.checked = this.selected.has(checkbox.value);
  }

  async fetchResults() {
    this.cancelSearch();
    this.abortController = new AbortController();

    const url = new URL(this.searchUrlValue, window.location.origin);
    url.searchParams.set("q", this.searchInputTarget.value.trim());
    url.searchParams.set("scope", this.scopeSelectTarget.value);

    try {
      const response = await fetch(url, {
        headers: { Accept: "text/html", "X-Requested-With": "XMLHttpRequest" },
        signal: this.abortController.signal
      });
      if (!response.ok) throw new Error(`Search failed (${response.status})`);
      this.resultsTarget.innerHTML = await response.text();
    } catch (error) {
      if (error.name === "AbortError") return;
      this.resultsTarget.innerHTML =
        '<p class="events-group-add-sessions-modal__hint">Could not load sessions. Try again.</p>';
    }
  }

  cancelSearch() {
    clearTimeout(this.searchTimer);
    if (this.abortController) {
      this.abortController.abort();
      this.abortController = null;
    }
  }

  syncSelection() {
    this.selectedInputsTarget.innerHTML = "";
    this.selected.forEach((_title, id) => {
      const input = document.createElement("input");
      input.type = "hidden";
      input.name = "event_ids[]";
      input.value = id;
      this.selectedInputsTarget.appendChild(input);
    });

    const count = this.selected.size;
    this.submitButtonTarget.disabled = count === 0;
    this.submitButtonTarget.textContent =
      count === 0 ? "Add Sessions" : `Add ${count} ${count === 1 ? "Session" : "Sessions"}`;
  }
}
